import type { EChartsOption, CustomSeriesRenderItem, CustomSeriesOption } from 'echarts';
import { DEFAULT_COLORS } from './tachograph-chart.colors';
import { DrawSegment, TimelineConfig, DayBounds } from './tachograph-chart.types';
import { formatDurationShort, formatTimeHM } from './tachograph-chart.utils';
import { TranslateService } from '@ngx-translate/core';
import { TachoWorkState } from '@src/app/interfaces/tachograph.interface';

export interface BuildOptionParams {
  data: DrawSegment[];
  bounds: DayBounds;
  config?: TimelineConfig;
  translateService: TranslateService,
}

export const getTachoStatusNameById = (status: TachoWorkState, translate: TranslateService): string => {
  return status === TachoWorkState.None ? translate.instant('tacho-status-none') : translate.instant(`tacho-status-${status}`);
};

export function buildTimelineOption({ data, bounds, config, translateService }: BuildOptionParams): EChartsOption {
  const colors = { ...DEFAULT_COLORS, ...(config?.colors ?? {}) };
  const labelMin = config?.labelMinWidthPx ?? 36;

  const HEIGHT_BY_STATE: Record<TachoWorkState, number> = {
    [TachoWorkState.Rest]: 40,
    [TachoWorkState.Ready]: 48,
    [TachoWorkState.Work]: 58,
    [TachoWorkState.Drive]: 68,
    [TachoWorkState.None]: 35,
  };
  const GAP_PX = 0;
  const maxBarHeight = Math.max(...Object.values(HEIGHT_BY_STATE));

  const gridHeight = Math.max(config?.trackHeightPx ?? 70, maxBarHeight + GAP_PX + 4);

  const renderBar: CustomSeriesRenderItem = (params, api) => {
    const from  = api.value(0) as number;
    const to    = api.value(1) as number;
    const state = api.value(2) as TachoWorkState;

    const xFrom = api.coord([from, 0])[0];
    const xTo   = api.coord([to,   0])[0];
    const width = Math.max(1, xTo - xFrom - 2);

    const barHeight = HEIGHT_BY_STATE[state] ?? 40;

    const cs = (params as any).coordSys;
    const gridBottom = cs.y + cs.height;

    const x = xFrom + 1;
    const y = gridBottom - GAP_PX - barHeight;
    const h = barHeight;

    return {
      type: 'rect',
      shape: { x, y, width, height: h },
      style: { fill: colors[state], stroke: 'rgba(0,0,0,0.12)', opacity: 0.80 },
      focus: 'self',
      styleEmphasis: { shadowBlur: 4, shadowColor: 'rgba(0,0,0,0.25)' }
    };
  };

  const renderLabel: CustomSeriesRenderItem = (params, api) => {
    const from = api.value(0) as number;
    const to   = api.value(1) as number;
    const dur  = api.value(3) as number;
    const state = api.value(2) as TachoWorkState;

    const xFrom = api.coord([from, 0])[0];
    const xTo   = api.coord([to,   0])[0];
    if (xTo - xFrom < labelMin) return;

    const cx = (xFrom + xTo) / 2;

    const barHeight = HEIGHT_BY_STATE[state] ?? 40;
    const cs = (params as any).coordSys;
    const gridBottom = cs.y + cs.height;
    const cy = (gridBottom - GAP_PX - barHeight) + barHeight / 2;

    return {
      type: 'text',
      style: {
        text: formatDurationShort(from, to),
        x: cx, y: cy,
        textAlign: 'center', textVerticalAlign: 'middle',
        fill: '#fff', fontSize: 11,
        textShadowBlur: 2, textShadowColor: 'rgba(0,0,0,.35)',
        fontWeight: 500, fontFamily: 'Open Sans, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif'
      },
      silent: true
    };
  };

  const baseData = data.map(d => [d.from, d.to, d.state, d.duration, d.rawFrom, d.rawTo]);

  const barSeries: CustomSeriesOption = {
    type: 'custom',
    name: 'Segments',
    renderItem: renderBar,
    data: baseData,
    encode: { x: [0, 1], y: 2 },
    z: 1, zlevel: 1
  };

  const labelSeries: CustomSeriesOption = {
    type: 'custom',
    name: 'Labels',
    renderItem: renderLabel,
    data: baseData,
    silent: true,
    z: 2, zlevel: 1
  };

  const option: EChartsOption = {
    animation: false,
    grid: {
      top: 6, left: 6, right: 6,
      height: gridHeight,
      bottom: 28,
      containLabel: false
    },
    xAxis: {
      type: 'time',
      min: bounds.start,
      max: bounds.end,
      splitNumber: 24,
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: true, lineStyle: { color: '#e5e7eb', width: 2 } },
      axisLabel: {
        formatter: (val: number) => {
          const h = new Date(val).getHours();
          return h === 0 ? '00' : String(h).padStart(2, '0');
        }
      },
      splitArea: { show: true, areaStyle: { color: ['#f9fafb', '#f9fafbb1'], opacity: 0.85 } }
    },
    yAxis: {
      type: 'value',
      min: 0, max: 1,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { show: false },
      splitLine: { show: false }
    },
    tooltip: {
      trigger: 'item',
      appendToBody: true,
      formatter: (p: any) => {
        const [from, to, state, dur, rawFrom, rawTo] =
          p.data as [number, number, TachoWorkState, number, number, number];
        return `
          <div>
            <div>${translateService.instant('date')}: <b>${formatTimeHM(rawFrom)}–${formatTimeHM(rawTo)}</b></div>
            <div>${translateService.instant('duration')}: ${formatDurationShort(from, to)}</div>
            <div>${translateService.instant('status')}: ${getTachoStatusNameById(state, translateService)}</div>
          </div>`;
      }
    },
    series: [barSeries, labelSeries]
  };

  return option;
}
